import React from 'react'
import _ from 'lodash'

import checkPermission from '../../../../../utilities/checkPermission'
import useAppFolderAndTabAction from '../../../../../stores/appFolderAndTab/appFolderAndTabAction'
import useTranslate from '../../../../../i18n/useTranslate'
import useManageProjectStore from '../../../../../stores/manageProject/useManageProjectStore'
import useWebService from '../../../../../services/web/useWebService'
import useLoading from '../../../../../control/loading/useLoading'
import authenStore from '../../../../../stores/authen/authenStore'
import useFolderAndTab from '../../useFolderAndTab'

import * as REQServiceType from '../../../../../services/web/types/requestWebServiceTypes'
import * as additionalDocumentType from './additionalDocumentType'

import { useHistory } from 'react-router-dom'
import { RoutePath } from '../../../../../router/routesConfig'
import { convertDateToDateString, dateForAPI } from '../../../../../utilities/date'
import { DialogType } from '../../../../../control/dialog/dialog'
import { TabSelectedType } from '../../../../../stores/appFolderAndTab/appFolderAndTabStore'

const useAdditionDocument = (props: additionalDocumentType.UseAdditionDocument) => {
    const { callbackSaveComplete } = props

    const [initialValues, setInitialValues] = React.useState<any>({})
    const { projectData } = useManageProjectStore()
    const [disabledField, setDisabledField] = React.useState<'all' | string[]>()
    const [requiredField, setRequiredField] = React.useState<'all' | string[]>([
        'requireDoc',
        'documentNumber',
        'sendDocumentDate',
    ])

    const checkPermissionHook = checkPermission()
    const isViewMode = checkPermissionHook.chkProjectStepViewMode(projectData)
    const isShowUpdateDate = checkPermissionHook.chkShowUpdateDate(projectData)

    const appFolderAndTabAction = useAppFolderAndTabAction()
    const history = useHistory()
    const translate = useTranslate()
    const callWebService = useWebService()
    const loadingHook = useLoading()
    const profileStore = authenStore()
    const { profile } = profileStore
    const [configDialog, setConfigDialog] =
        React.useState<Pick<DialogType, 'title' | 'content' | 'type' | 'onClickConfirm' | 'onAfterClose'>>()
    const dialogRef = React.useRef<Record<string, Function>>()
    const paramsRef = React.useRef<any>()
    const tabAndFolderHook = useFolderAndTab()

    React.useEffect(() => {
        if (projectData) {
            createInitialData()

            if (projectData.isRequestDocument != null) {
                //edit form
                if (projectData.isRequestDocument) {
                    setDisabledField(undefined)
                    setRequiredField(['requireDoc', 'documentNumber', 'sendDocumentDate'])
                } else {
                    setDisabledField(['documentNumber', 'sendDocumentDate', 'remark'])
                    setRequiredField(['requireDoc'])
                }
            }
        }

        if (isViewMode) {
            setDisabledField('all')
        }
    }, [projectData?.projectID ?? null, projectData?.submittedID ?? null, projectData?.isDraft ?? null])

    const onRefDialog = (func: Record<string, Function>) => {
        //ห้าม set State หรือเรียก Action ในนี้
        dialogRef.current = func
    }

    const createInitialData = () => {
        if (projectData) {
            const isDefaultRequest = projectData?.isRequestDocument ?? true
            const initialData = {
                documentNumber: projectData?.documentNumber ? `${projectData.documentNumber}` : null,
                sendDocumentDate: isDefaultRequest
                    ? projectData?.sendDocumentDate
                        ? projectData.sendDocumentDate
                        : new Date()
                    : null,
                remark: projectData?.documentRemark ? projectData.documentRemark : null,
                isRequestDocument: projectData?.isRequestDocument,
                requireDoc: getInitialRequireDoc(projectData.isRequestDocument),
            }
            setInitialValues(initialData)
        }
    }

    const getInitialRequireDoc = (isRequestDocument: boolean) => {
        let initRequireDoc
        if (isRequestDocument != null) {
            if (isRequestDocument) {
                initRequireDoc = { label: translate.get('LABEL.NEED'), value: '1' }
            } else {
                initRequireDoc = { label: translate.get('LABEL.NOT_NEED'), value: '0' }
            }
        } else {
            initRequireDoc = { label: translate.get('LABEL.NEED'), value: '1' }
        }

        return initRequireDoc
    }

    const handleClickCancel = (dirty: boolean) => {
        if (dirty) {
            setConfigDialog({
                title: translate.get('ALERT.TITLE.WARNING'),
                content: translate.get('ALERT.CONTENT.NEED_TO_CANCEL'),
                type: 'alert-confirm',

                onClickConfirm: () => {
                    history.push(RoutePath.manageProject)
                },
            })
            dialogRef.current?.openDialog()
        } else {
            history.push(RoutePath.manageProject)
        }
    }

    const handleSubmitForm = async (val: any) => {
        const parameter = getParameterForSave(val)
        if (val.submitType === 'save') {
            setConfigDialog({
                title: translate.get('ALERT.TITLE.NOTIFICATION'),
                content: translate.get('ALERT.CONTENT.NEED_TO_SAVE'),
                type: 'alert-confirm',
                onClickConfirm: () => {
                    handleConfirmSave(parameter)
                },
            })
            dialogRef.current?.openDialog()
        } else {
            //ถ้า save draft ไม่ต้องมี alert confirm
            handleConfirmSave(parameter)
        }
    }
    const getParameterForSave = (val: any) => {
        if (projectData) {
            let isDraft = false
            if (val.submitType === 'draft') {
                isDraft = true
            }
            const reqNumberOfDocType: REQServiceType.RequestNumberOfDocType = {
                loginUserID: profile.userId,
                projectID: projectData.projectID,
                submittedID: projectData.submittedID,
                isRequestDoc: val?.requireDoc?.value === '1' ? true : false,
                documentNumber: val?.documentNumber ?? null,
                sendDocumentDate:
                    val && val.sendDocumentDate && val.sendDocumentDate
                        ? dateForAPI(val?.sendDocumentDate) ?? null
                        : null,
                remark: val.remark ? val.remark : null,
                isDraft: isDraft,
            }
            return reqNumberOfDocType
        }
    }

    const handleConfirmSave = async (
        paramForSave: REQServiceType.RequestNumberOfDocType | undefined,
        tabIndex: number | null = null,
        newTabSelected: TabSelectedType | null = null,
        isFolder?: boolean,
    ) => {
        if (paramForSave) {
            dialogRef.current?.closeDialog()
            const isDraft = paramForSave.isDraft ? paramForSave.isDraft : false
            loadingHook.loadingOpen()
            const res = await callWebService.requestNumberOfDocument(paramForSave)

            if (!isFolder) {
                //if นี้จะเช็คว่าเป็นการติด dirty จากการกดเปลี่ยน folder ใช่หรือไม่?
                if (tabIndex && newTabSelected) {
                    //if นี้จะเช็คว่าเป็นการติด dirty จากการกดเปลี่ยน Tab ใช่หรือไม่?
                    tabAndFolderHook.setTab(tabIndex, newTabSelected)
                } else {
                    //หากเข้า else หมายความว่าเป็นการบันทึกจากกดปุ่มบันทึกไม่ใช่จากการติด dirty
                    if (callbackSaveComplete) {
                        if (res && res.status) {
                            callbackSaveComplete(res.status, isDraft)
                        }
                    }
                }
            }
            loadingHook.loadingClose()
        }
    }

    const setPropsWhenRequestDoc = (isRequest: string) => {
        if (isRequest && isRequest === '0') {
            //เลือกไม่ต้องการจะ disable ฟิล จำนวน วันที่และหมายเหตุ และ RequiedField แค่ requireDoc
            setDisabledField(['documentNumber', 'sendDocumentDate', 'remark'])
            setRequiredField(['requireDoc'])
        } else {
            setDisabledField(undefined)
            setRequiredField(['requireDoc', 'documentNumber', 'sendDocumentDate'])
        }
    }
    /**
     *
     * Dirty
     */
    const handleDirtyChange = (props: any) => {
        let isDirty: boolean = false
        let newFormValue = { ...props.values, sendDocumentDate: convertDateToDateString(props.values.sendDocumentDate) }
        let initValue = { ...initialValues, sendDocumentDate: convertDateToDateString(initialValues.sendDocumentDate) }

        const isEqual = _.isEqual(initValue, newFormValue)
        if (!isViewMode) {
            if (isEqual) {
                isDirty = false
            } else {
                isDirty = true
            }
        }
        // appFolderAndTabAction.setTabDirty(isDirty)
        paramsRef.current = getParameterForSave(props.values)
        const paramsDirty = {
            tabDirty: isDirty,
            callbackDirty: callbackDirtyProcess,
        }
        appFolderAndTabAction.setDirty(paramsDirty)
    }

    const callbackDirtyProcess = (
        tabIndex: number | null = null,
        newTabSelected: TabSelectedType | null = null,
        isFolder?: boolean,
    ) => {
        if (paramsRef.current) {
            paramsRef.current.isDraft = true
            handleConfirmSave(paramsRef.current, tabIndex, newTabSelected, isFolder)
        }
    }
    return {
        isViewMode,
        initialValues,
        configDialog,
        disabledField,
        requiredField,
        isShowUpdateDate,
        onRefDialog,
        onSubmitForm: handleSubmitForm,
        funcCallBack: setPropsWhenRequestDoc,
        onClickCancel: handleClickCancel,
        onDirtyChange: handleDirtyChange,
    }
}

export default useAdditionDocument
